// ============================================================
// escritorioPage.js — Lógica de la página Escritorio
// ------------------------------------------------------------
// Carga las creaciones recientes del usuario desde la API y las muestra
// en la cuadrícula de creaciones recientes del escritorio (máx 4).
// ============================================================
import { qs, qsa } from './utils.js';
import { listHistory } from './api.js';

export async function initEscritorioPage() {
  if (document.body.dataset.page !== 'escritorio') return;

  const grid = qs('#recentGallery');
  if (!grid) return;

  // Mostrar indicador de carga
  grid.innerHTML = `
    <div class="text-center py-5 w-100" id="recentLoading" style="grid-column: span 4;">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
      <p class="mt-2 text-muted">Cargando tus creaciones recientes...</p>
    </div>
  `;

  try {
    const items = await listHistory();
    const completedItems = items.filter(item => item.status === 'completed');

    grid.innerHTML = ''; // Limpiar placeholders y loader

    if (completedItems.length === 0) {
      grid.innerHTML = `
        <div class="text-center py-5 w-100" style="grid-column: span 4;">
          <i class="bi bi-folder-x" style="font-size: 3rem; color: var(--text-muted);"></i>
          <h3 class="mt-3">No tienes creaciones recientes</h3>
          <p class="text-muted">Las imágenes, vídeos o avatares que generes aparecerán aquí.</p>
        </div>
      `;
      return;
    }

    // Tomar las 4 creaciones más recientes
    const recentItems = completedItems.slice(0, 4);

    // Elementos del modal de vista previa
    const modal = qs('#previewModal');
    const mediaContainer = qs('#previewMediaContainer');
    const promptEl = qs('#previewPrompt');
    const closeBtn = qs('#closePreviewBtn');

    function openPreviewModal(item) {
      if (!modal || !mediaContainer || !promptEl) return;
      
      mediaContainer.innerHTML = '';
      
      const isVideoUrl = item.url && !/\.(png|jpe?g|gif|webp)$/i.test(item.url);
      if (isVideoUrl) {
        const video = document.createElement('video');
        video.src = item.url;
        video.controls = true;
        video.autoplay = true;
        video.style.width = '100%';
        video.style.height = '100%';
        video.style.objectFit = 'contain';
        mediaContainer.appendChild(video);
      } else {
        const imgContainer = document.createElement('div');
        imgContainer.style.cssText = 'position:relative; width:100%; height:100%; display:flex; align-items:center; justify-content:center; background:#000; overflow:hidden; border-radius:8px;';
        
        const img = document.createElement('img');
        img.src = item.url || item.thumb;
        img.style.cssText = 'max-width:100%; max-height:100%; object-fit:contain; transition: transform 0.3s ease, filter 0.3s ease;';
        imgContainer.appendChild(img);

        if (item.type === 'avatar') {
          const playSpeechBtn = document.createElement('button');
          playSpeechBtn.type = 'button';
          playSpeechBtn.style.cssText = 'position:absolute; bottom:16px; left:50%; transform:translateX(-50%); background:linear-gradient(135deg, #a855f7, #ec4899); color:white; border:none; padding:10px 22px; border-radius:24px; font-weight:600; font-size:0.9rem; cursor:pointer; display:flex; align-items:center; gap:8px; box-shadow:0 4px 15px rgba(168,85,247,0.5); z-index:20;';
          playSpeechBtn.innerHTML = `<i class="bi bi-volume-up-fill" style="font-size:1.1rem;"></i> <span>Reproducir Voz Avatar</span>`;

          playSpeechBtn.addEventListener('click', () => {
            if ('speechSynthesis' in window) {
              window.speechSynthesis.cancel();
              const textToSpeak = item.prompt || item.title || 'Feliz cumpleaños';
              const utter = new SpeechSynthesisUtterance(textToSpeak);
              utter.lang = 'es-ES';
              utter.rate = 0.95;
              
              img.style.transform = 'scale(1.05)';
              img.style.filter = 'brightness(1.15)';
              
              utter.onend = () => {
                img.style.transform = 'scale(1)';
                img.style.filter = 'none';
              };
              utter.onerror = () => {
                img.style.transform = 'scale(1)';
                img.style.filter = 'none';
              };
              
              window.speechSynthesis.speak(utter);
            }
          });
          
          imgContainer.appendChild(playSpeechBtn);
        }

        mediaContainer.appendChild(imgContainer);
      }
      
      promptEl.textContent = item.title || item.prompt || '';
      
      modal.style.display = 'flex';
      setTimeout(() => {
        modal.style.opacity = '1';
        modal.firstElementChild.style.transform = 'scale(1)';
      }, 10);
    }

    function closePreviewModal() {
      if (!modal) return;
      modal.style.opacity = '0';
      modal.firstElementChild.style.transform = 'scale(0.9)';
      
      const video = mediaContainer?.querySelector('video');
      if (video) {
        video.pause();
      }
      
      setTimeout(() => {
        modal.style.display = 'none';
        if (mediaContainer) mediaContainer.innerHTML = '';
      }, 300);
    }

    if (closeBtn) {
      closeBtn.addEventListener('click', closePreviewModal);
    }
    if (modal) {
      modal.addEventListener('click', (e) => {
        if (e.target === modal) closePreviewModal();
      });
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal && modal.style.display === 'flex') {
        closePreviewModal();
      }
    });

    recentItems.forEach(item => {
      const card = document.createElement('div');
      card.className = 'gen-card has-result';
      card.style.cursor = 'pointer';
      
      const isVideo = item.type === 'video' || item.type === 'avatar' || item.type === 'template';
      const isImageUrl = item.url && /\.(png|jpe?g|gif|webp|avif)$/i.test(item.url);
      
      let mediaHtml = '';
      if (isVideo && !isImageUrl) {
        mediaHtml = `
          <video src="${item.url}" poster="${item.thumb || ''}" muted loop playsinline style="width:100%; height:100%; object-fit:cover; border-radius:12px;"></video>
          <span class="history-thumb-play" style="position:absolute; top:50%; left:50%; transform:translate(-50%, -50%); background:rgba(0,0,0,0.6); color:white; border-radius:50%; width:40px; height:40px; display:flex; align-items:center; justify-content:center; pointer-events:none;">
            <i class="bi bi-play-fill" style="font-size: 1.5rem; margin-left: 2px;"></i>
          </span>
        `;
      } else {
        mediaHtml = `<img src="${item.url}" alt="${item.title}" style="width:100%; height:100%; object-fit:cover; border-radius:12px;" />`;
      }

      card.innerHTML = `
        ${mediaHtml}
        <div class="gen-card-actions">
          <button class="gen-card-action" type="button" aria-label="Descargar" data-download-url="${item.url}">
            <i class="bi bi-download"></i>
          </button>
        </div>
      `;

      // Evento de click para abrir modal de vista previa
      card.addEventListener('click', (e) => {
        if (e.target.closest('.gen-card-action')) return;
        openPreviewModal(item);
      });

      // Autoplay al pasar el ratón (hover)
      if (isVideo && !isImageUrl) {
        const video = card.querySelector('video');
        if (video) {
          card.addEventListener('mouseenter', () => video.play().catch(() => {}));
          card.addEventListener('mouseleave', () => {
            video.pause();
            video.currentTime = 0;
          });
        }
      }

      grid.appendChild(card);
    });

  } catch (err) {
    console.error('[Escritorio] Error al cargar creaciones recientes:', err);
    grid.innerHTML = `
      <div class="alert alert-danger w-100" role="alert" style="grid-column: span 4;">
        Error al cargar tus creaciones recientes.
      </div>
    `;
  }
}
